/**
 * Performance Logger — collects timing data during page load and sends to backend.
 *
 * Usage:
 *   import { perfLogger } from '../utils/perf-logger';
 *   const perf = perfLogger.start('/');           // start a session for a route
 *   perf.mark('dns-resolve');                      // mark start of a stage
 *   await dnsResolve();
 *   perf.measure('dns-resolve');                   // measure duration since mark
 *   ...
 *   perf.send(apiUrl);                             // POST all collected timings to backend
 */

class PerfSession {
    constructor(route) {
        this.route = route;
        this.pageStart = performance.now();
        this.navigationStart = performance.timeOrigin || Date.now() - performance.now();
        this.marks = {};
        this.timings = [];
        this.metadata = {};
        this.sent = false;
    }

    mark(name) {
        this.marks[name] = performance.now();
    }

    measure(name) {
        if (this.marks[name] !== undefined) {
            const duration = Math.round(performance.now() - this.marks[name]);
            this.timings.push({ stage: name, duration, timestamp: Date.now() });
            delete this.marks[name];
            return duration;
        }
        return 0;
    }

    addMetadata(obj) {
        Object.assign(this.metadata, obj);
    }

    addTiming(stage, duration) {
        this.timings.push({ stage, duration: Math.round(duration), timestamp: Date.now() });
    }

    async send(apiUrl) {
        if (this.sent) return;
        this.sent = true;

        const totalDuration = Math.round(performance.now() - this.pageStart);
        this.timings.push({ stage: 'total', duration: totalDuration, timestamp: Date.now() });

        // Add browser performance entries if available
        try {
            const navEntry = performance.getEntriesByType('navigation')[0];
            if (navEntry) {
                this.metadata.navigationTiming = {
                    domContentLoaded: Math.round(navEntry.domContentLoadedEventEnd),
                    domComplete: Math.round(navEntry.domComplete),
                    loadEvent: Math.round(navEntry.loadEventEnd),
                    responseEnd: Math.round(navEntry.responseEnd),
                    connectEnd: Math.round(navEntry.connectEnd),
                    domInteractive: Math.round(navEntry.domInteractive),
                    fetchStart: Math.round(navEntry.fetchStart),
                    transferSize: navEntry.transferSize,
                    type: navEntry.type
                };
            }
        } catch (e) {}

        // Collect resource timings for API calls
        try {
            const resources = performance.getEntriesByType('resource');
            const apiResources = resources
                .filter(r => r.name.includes('/api/') || r.name.includes('dns-resolver'))
                .map(r => ({
                    name: r.name.split('?')[0].replace(/https?:\/\/[^/]+/, ''),
                    duration: Math.round(r.duration),
                    startTime: Math.round(r.startTime),
                    transferSize: r.transferSize || 0
                }));
            if (apiResources.length > 0) {
                this.metadata.resourceTimings = apiResources;
            }
        } catch (e) {}

        const payload = {
            route: this.route,
            source: 'frontend',
            timings: this.timings,
            totalDuration,
            metadata: {
                ...this.metadata,
                href: window.location.href,
                screenWidth: window.innerWidth,
                screenHeight: window.innerHeight,
                connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown'
            }
        };

        try {
            // Use fetch instead of axios to avoid interceptor overhead/interference
            const url = `${apiUrl}debug-performance-log?url=${encodeURIComponent(window.location.hostname)}`;
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true  // ensure it sends even if page is unloading
            }).catch(() => {});  // fire and forget
        } catch (e) {
            // Silently fail — this is debug logging, should never break the app
        }
    }
}

class PerfLogger {
    constructor() {
        this.sessions = {};
    }

    start(route) {
        const session = new PerfSession(route);
        this.sessions[route] = session;
        return session;
    }

    get(route) {
        return this.sessions[route] || null;
    }
}

export const perfLogger = new PerfLogger();
